// ================================================================
// WeatherPage.jsx — نسخه‌ی نهایی یکپارچه
// ================================================================
import React, {
  useState, useEffect, useMemo, useCallback, useRef,
  createContext, useContext,
} from 'react';
import './WeatherPage.css';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';   // 🆕

// ================================================================
// SECTION 1: CONSTANTS
// ================================================================

const API_BASE = 'https://api.open-meteo.com/v1/forecast';
const GEO_API = 'https://geocoding-api.open-meteo.com/v1/search';

const TIMEZONE = 'Asia/Tehran';  // ✅ همه‌ی زمان‌ها به وقت ایران

// ✅ کلیدهای per-user (برای جداسازی کاربران)
// نکته: CACHE سراسریه چون فقط دیتای API بر اساس مختصات جغرافیاییه
// و هیچ اطلاعات هویتی از کاربر نداره.
const buildStorageKeys = (userId) => ({
  CITIES:       `weather_cities_v2:${userId}`,
  ACTIVE_CITY:  `weather_active_city_v2:${userId}`,
  SETTINGS:     `weather_settings_v2:${userId}`,
  CACHE:        'weather_cache_v2',
  PRIMARY_CITY: `weather_primary_city_v2:${userId}`,
});


const DEFAULT_CITY = {
  id: 'tehran',
  name: 'تهران',
  country: 'ایران',
  admin1: 'تهران',
  latitude: 35.6892,
  longitude: 51.389,
  timezone: TIMEZONE,
};

const DEFAULT_SETTINGS = {
  tempUnit: 'celsius',   // celsius | fahrenheit
  windUnit: 'kmh',       // kmh | ms | mph
  darkMode: false,
  timeFormat: '24',      // 24 | 12
};

// ✅ پارامترهای تأییدشده (بدون پارامترهای نامعتبر)
const CURRENT_PARAMS = [
  'temperature_2m',
  'relative_humidity_2m',
  'apparent_temperature',
  'is_day',
  'precipitation',
  'weather_code',
  'cloud_cover',
  'surface_pressure',
  'wind_speed_10m',
  'wind_direction_10m',
].join(',');

const HOURLY_PARAMS = [
  'temperature_2m',
  'relative_humidity_2m',
  'apparent_temperature',
  'precipitation_probability',
  'precipitation',
  'weather_code',
  'is_day',
  'wind_speed_10m',
  'wind_direction_10m',
  'uv_index',
  'visibility',
  'cloud_cover',
  'dew_point_2m',
].join(',');

const DAILY_PARAMS = [
  'weather_code',
  'temperature_2m_max',
  'temperature_2m_min',
  'precipitation_probability_max',
  'sunrise',
  'sunset',
  'uv_index_max',
  'wind_speed_10m_max',
  'wind_direction_10m_dominant',
].join(',');

// ================================================================
// SECTION 2: WMO CODES → Persian
// ================================================================

const WMO_MAP = {
  0:  { label: 'آسمان صاف',        icon: 'clear',        grad: 'sunny'  },
  1:  { label: 'عمدتاً صاف',       icon: 'mostly-clear', grad: 'sunny'  },
  2:  { label: 'نیمه‌ابری',        icon: 'partly-cloudy',grad: 'cloudy' },
  3:  { label: 'ابری',              icon: 'overcast',     grad: 'cloudy' },
  45: { label: 'مه',                icon: 'fog',          grad: 'fog'    },
  48: { label: 'مه یخ‌زده',        icon: 'fog',          grad: 'fog'    },
  51: { label: 'نم‌نم باران سبک',  icon: 'drizzle',      grad: 'rain'   },
  53: { label: 'نم‌نم باران',       icon: 'drizzle',      grad: 'rain'   },
  55: { label: 'نم‌نم باران شدید',  icon: 'drizzle',      grad: 'rain'   },
  56: { label: 'نم‌نم یخ‌زده',      icon: 'sleet',        grad: 'snow'   },
  57: { label: 'نم‌نم یخ‌زده شدید', icon: 'sleet',        grad: 'snow'   },
  61: { label: 'باران سبک',         icon: 'rain',         grad: 'rain'   },
  63: { label: 'باران',              icon: 'rain',         grad: 'rain'   },
  65: { label: 'باران شدید',        icon: 'rain',         grad: 'rain'   },
  66: { label: 'باران یخ‌زده',      icon: 'sleet',        grad: 'snow'   },
  67: { label: 'باران یخ‌زده شدید', icon: 'sleet',        grad: 'snow'   },
  71: { label: 'برف سبک',           icon: 'snow',         grad: 'snow'   },
  73: { label: 'برف',                icon: 'snow',         grad: 'snow'   },
  75: { label: 'برف شدید',          icon: 'snow',         grad: 'snow'   },
  77: { label: 'دانه‌های برف',      icon: 'snow',         grad: 'snow'   },
  80: { label: 'رگبار سبک',         icon: 'showers',      grad: 'rain'   },
  81: { label: 'رگبار',              icon: 'showers',      grad: 'rain'   },
  82: { label: 'رگبار شدید',         icon: 'showers',      grad: 'rain'   },
  85: { label: 'رگبار برف',          icon: 'snow-showers', grad: 'snow'   },
  86: { label: 'رگبار برف شدید',    icon: 'snow-showers', grad: 'snow'   },
  95: { label: 'رعد و برق',         icon: 'thunder',      grad: 'storm'  },
  96: { label: 'رعد و برق با تگرگ', icon: 'thunder',      grad: 'storm'  },
  99: { label: 'رعد و برق شدید',    icon: 'thunder',      grad: 'storm'  },
};

const getWMO = (code) => WMO_MAP[code] || WMO_MAP[0];

// ================================================================
// SECTION 3: HELPERS (همه با تایم‌زون ایران)
// ================================================================

const toFa = (n) => {
  if (n === undefined || n === null || Number.isNaN(n)) return '—';
  return String(n).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);
};

const safeNum = (val, fallback = 0) =>
  (val !== undefined && val !== null && !Number.isNaN(val)) ? val : fallback;

// ✅ همه‌ی محاسبات زمانی به تایم‌زون ایران
const formatTime = (dateInput, format = '24') => {
  if (!dateInput) return '—';
  try {
    const d = new Date(dateInput);
    if (Number.isNaN(d.getTime())) return '—';
    const opts = format === '12'
      ? { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: TIMEZONE }
      : { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TIMEZONE };
    return new Intl.DateTimeFormat('fa-IR', opts).format(d);
  } catch { return '—'; }
};

const formatJalali = (dateInput, opts = {}) => {
  if (!dateInput) return '—';
  try {
    const d = new Date(dateInput);
    if (Number.isNaN(d.getTime())) return '—';
    const base = { timeZone: TIMEZONE, calendar: 'persian' };
    const fmt = new Intl.DateTimeFormat('fa-IR', { ...base, ...opts });
    return fmt.format(d);
  } catch { return '—'; }
};

const formatDayName = (dateInput) =>
  formatJalali(dateInput, { weekday: 'long' });

const formatDayShort = (dateInput) =>
  formatJalali(dateInput, { day: 'numeric', month: 'short' });

const formatDateFull = (dateInput) =>
  formatJalali(dateInput, { day: 'numeric', month: 'long', year: 'numeric' });

// ✅ یافتن ایندکس ساعت فعلی در آرایه‌ی hourly (به وقت ایران)
const findCurrentHourIndex = (hourlyTime) => {
  if (!Array.isArray(hourlyTime) || hourlyTime.length === 0) return 0;

  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', hour12: false,
  }).formatToParts(now).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});

  const hourStr = parts.hour === '24' ? '00' : parts.hour;
  const target = `${parts.year}-${parts.month}-${parts.day}T${hourStr}:00`;

  const idx = hourlyTime.findIndex((t) => t.startsWith(target.slice(0, 13)));
  if (idx >= 0) return idx;

  // fallback: اولین ساعت >= الان
  for (let i = 0; i < hourlyTime.length; i++) {
    if (new Date(hourlyTime[i]).getTime() >= now.getTime() - 3600000) return i;
  }
  return 0;
};







// ✅ Reverse Geocoding با Nominatim (تبدیل مختصات به نام شهر)
const reverseGeocode = async (latitude, longitude) => {
  // بررسی کش (TTL: ۲۴ ساعت)
  const cacheKey = `geo_${latitude.toFixed(3)}_${longitude.toFixed(3)}`;
  try {
    const cached = JSON.parse(localStorage.getItem('weather_geocode_cache') || '{}');
    if (cached[cacheKey] && Date.now() - cached[cacheKey].ts < 24 * 60 * 60 * 1000) {
      return cached[cacheKey].data;
    }
  } catch {}

  const url = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=jsonv2&accept-language=fa&zoom=10`;

  try {
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'WeatherApp/2.0 (contact@weatherapp.example)',
      },
    });
    if (!response.ok) throw new Error(`Nominatim HTTP ${response.status}`);
    const data = await response.json();
    const addr = data.address || {};

    const cityName =
      addr.city ||
      addr.town ||
      addr.village ||
      addr.hamlet ||
      addr.county ||
      addr.state ||
      'موقعیت شما';

    const result = {
      name: cityName,
      country: addr.country || '—',
      admin1: addr.state || addr.county || '',
      displayName: data.display_name || cityName,
    };

    // ذخیره در کش
    try {
      const cache = JSON.parse(localStorage.getItem('weather_geocode_cache') || '{}');
      cache[cacheKey] = { ts: Date.now(), data: result };
      // پاکسازی کش‌های قدیمی (بیش از ۱۰۰ آیتم)
      const keys = Object.keys(cache);
      if (keys.length > 100) {
        const oldest = keys.sort((a, b) => (cache[a].ts || 0) - (cache[b].ts || 0))[0];
        delete cache[oldest];
      }
      localStorage.setItem('weather_geocode_cache', JSON.stringify(cache));
    } catch {}

    return result;
  } catch (err) {
    console.warn('Reverse geocoding failed:', err.message);
    return null;
  }
};





// ✅ واحدها
const convertTemp = (celsius, unit) => {
  const c = safeNum(celsius, 0);
  if (unit === 'fahrenheit') return Math.round((c * 9 / 5) + 32);
  return Math.round(c);
};

const tempSymbol = (unit) => unit === 'fahrenheit' ? '°F' : '°C';

const convertWind = (kmh, unit) => {
  const v = safeNum(kmh, 0);
  if (unit === 'ms') return (v / 3.6).toFixed(1);
  if (unit === 'mph') return (v / 1.609).toFixed(1);
  return Math.round(v);
};

const windSymbol = (unit) => {
  if (unit === 'ms') return 'm/s';
  if (unit === 'mph') return 'mph';
  return 'km/h';
};

// ✅ توصیفات
const dewPointDesc = (dp) => {
  if (dp < 5) return 'خشک';
  if (dp < 12) return 'راحت';
  if (dp < 17) return 'کمی مرطوب';
  if (dp < 22) return 'مرطوب';
  return 'بسیار شرجی';
};

const uvDesc = (uv) => {
  if (uv <= 2) return { text: 'پایین', tone: 'good' };
  if (uv <= 5) return { text: 'متوسط', tone: 'mid' };
  if (uv <= 7) return { text: 'بالا', tone: 'warn' };
  if (uv <= 10) return { text: 'بسیار بالا', tone: 'bad' };
  return { text: 'خطرناک', tone: 'danger' };
};

const humidityDesc = (h) => {
  if (h < 30) return 'خشک';
  if (h < 60) return 'معتدل';
  if (h < 80) return 'مرطوب';
  return 'بسیار مرطوب';
};

const windDir = (deg) => {
  const dirs = ['شمال', 'شمال‌شرقی', 'شرق', 'جنوب‌شرقی', 'جنوب', 'جنوب‌غربی', 'غرب', 'شمال‌غربی'];
  return dirs[Math.round(safeNum(deg, 0) / 45) % 8];
};

const windDesc = (spd) => {
  if (spd < 6) return 'آرام';
  if (spd < 20) return 'نسیم ملایم';
  if (spd < 40) return 'بادی';
  return 'طوفانی';
};

const pressureDesc = (p) => {
  if (p < 1000) return 'کم — احتمال بارش';
  if (p < 1013) return 'کمی پایین';
  if (p < 1025) return 'نرمال';
  return 'بالا — آسمان صاف';
};

const visibilityDesc = (v) => {
  if (v >= 20000) return 'عالی';
  if (v >= 10000) return 'خوب';
  if (v >= 5000) return 'متوسط';
  return 'ضعیف';
};

// ================================================================
// SECTION 4: WEATHER ICON (SVG)
// ================================================================

function WeatherIcon({ type, isDay = true, size = 48 }) {
  const s = size;
  const day = isDay;
  const Wrap = ({ children }) => (
    <svg width={s} height={s} viewBox="0 0 64 64" className="wico">{children}</svg>
  );

  switch (type) {
    case 'clear': return (
      <Wrap>
        <circle cx="32" cy="32" r="12" fill="#fbbf24" />
        <g stroke="#f59e0b" strokeWidth="2.4" strokeLinecap="round">
          <line x1="32" y1="6" x2="32" y2="13" />
          <line x1="32" y1="51" x2="32" y2="58" />
          <line x1="6" y1="32" x2="13" y2="32" />
          <line x1="51" y1="32" x2="58" y2="32" />
          <line x1="13.5" y1="13.5" x2="18.5" y2="18.5" />
          <line x1="45.5" y1="45.5" x2="50.5" y2="50.5" />
          <line x1="45.5" y1="18.5" x2="50.5" y2="13.5" />
          <line x1="13.5" y1="50.5" x2="18.5" y2="45.5" />
        </g>
      </Wrap>
    );
    case 'mostly-clear': return (
      <Wrap>
        <circle cx="24" cy="26" r="10" fill="#fbbf24" />
        <g stroke="#f59e0b" strokeWidth="2" strokeLinecap="round">
          <line x1="24" y1="6" x2="24" y2="11" />
          <line x1="6" y1="26" x2="11" y2="26" />
          <line x1="11" y1="13" x2="14.5" y2="16.5" />
          <line x1="37" y1="13" x2="33.5" y2="16.5" />
        </g>
        <path d="M22 46c0-5 4-9 9-9 1.5 0 3 .4 4.2 1.1C36.5 34 40 32 44 32c7 0 12 5 12 12s-5 12-12 12H22c-4 0-7-3-7-7s3-7 7-7z" fill="#e2e8f0" opacity="0.95" />
      </Wrap>
    );
    case 'partly-cloudy': return (
      <Wrap>
        {day && <circle cx="22" cy="24" r="9" fill="#fbbf24" />}
        {!day && <path d="M22 15a9 9 0 0 1 8 13 9 9 0 1 1-16 0 9 9 0 0 1 8-13z" fill="#cbd5e1" />}
        <path d="M16 46c0-5 4-9 9-9 1.5 0 3 .4 4.2 1.1C30.5 34 34 32 38 32c7 0 12 5 12 12s-5 12-12 12H16c-4 0-7-3-7-7s3-7 7-7z" fill="#94a3b8" />
      </Wrap>
    );
    case 'overcast': return (
      <Wrap>
        <path d="M14 42c0-5 4-9 9-9 1.5 0 3 .4 4.2 1.1C28.5 30 32 28 36 28c7 0 12 5 12 12s-5 12-12 12H14c-4 0-7-3-7-7s3-7 7-7z" fill="#94a3b8" />
      </Wrap>
    );
    case 'fog': return (
      <Wrap>
        <path d="M18 40c0-4 3-7 7-7 1 0 2 .2 3 .7C29 30 32 28 36 28c5 0 9 4 9 9s-4 9-9 9H18c-3 0-5-2-5-5s2-5 5-5z" fill="#cbd5e1" />
        <g stroke="#e2e8f0" strokeWidth="2.4" strokeLinecap="round" opacity="0.8">
          <line x1="14" y1="50" x2="50" y2="50" />
          <line x1="18" y1="56" x2="46" y2="56" />
        </g>
      </Wrap>
    );
    case 'drizzle':
    case 'rain':
    case 'showers': return (
      <Wrap>
        <path d="M16 38c0-5 4-9 9-9 1.5 0 3 .4 4.2 1.1C30.5 26 34 24 38 24c7 0 12 5 12 12s-5 12-12 12H16c-4 0-7-3-7-7s3-7 7-7z" fill="#94a3b8" />
        <g stroke="#60a5fa" strokeWidth="2.4" strokeLinecap="round">
          <line x1="20" y1="50" x2="18" y2="58" />
          <line x1="30" y1="50" x2="28" y2="58" />
          <line x1="40" y1="50" x2="38" y2="58" />
          <line x1="50" y1="50" x2="48" y2="58" />
        </g>
      </Wrap>
    );
    case 'snow':
    case 'snow-showers': return (
      <Wrap>
        <path d="M16 38c0-5 4-9 9-9 1.5 0 3 .4 4.2 1.1C30.5 26 34 24 38 24c7 0 12 5 12 12s-5 12-12 12H16c-4 0-7-3-7-7s3-7 7-7z" fill="#cbd5e1" />
        <g fill="#e0f2fe">
          <circle cx="20" cy="52" r="2.4" /><circle cx="32" cy="52" r="2.4" />
          <circle cx="44" cy="52" r="2.4" /><circle cx="26" cy="58" r="2" />
          <circle cx="38" cy="58" r="2" />
        </g>
      </Wrap>
    );
    case 'sleet': return (
      <Wrap>
        <path d="M16 38c0-5 4-9 9-9 1.5 0 3 .4 4.2 1.1C30.5 26 34 24 38 24c7 0 12 5 12 12s-5 12-12 12H16c-4 0-7-3-7-7s3-7 7-7z" fill="#94a3b8" />
        <g stroke="#60a5fa" strokeWidth="2.2" strokeLinecap="round">
          <line x1="22" y1="50" x2="21" y2="58" />
          <line x1="42" y1="50" x2="41" y2="58" />
        </g>
        <circle cx="32" cy="54" r="2.2" fill="#e0f2fe" />
      </Wrap>
    );
    case 'thunder': return (
      <Wrap>
        <path d="M16 36c0-5 4-9 9-9 1.5 0 3 .4 4.2 1.1C30.5 24 34 22 38 22c7 0 12 5 12 12s-5 12-12 12H16c-4 0-7-3-7-7s3-7 7-7z" fill="#475569" />
        <path d="M32 42l-6 10h6l-3 8 10-12h-6l4-6z" fill="#fbbf24" />
      </Wrap>
    );
    default: return <Wrap><circle cx="32" cy="32" r="12" fill="#fbbf24" /></Wrap>;
  }
}

// ================================================================
// SECTION 5: PULL-TO-REFRESH HOOK
// ================================================================

function usePullToRefresh(onRefresh, { threshold = 80, maxDistance = 140 } = {}) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startYRef = useRef(0);
  const pullingRef = useRef(false);

  useEffect(() => {
    const getScrollTop = () =>
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const onTouchStart = (e) => {
      if (isRefreshing) return;
      if (getScrollTop() > 4) return;
      startYRef.current = e.touches[0].clientY;
      pullingRef.current = true;
    };

    const onTouchMove = (e) => {
      if (!pullingRef.current || isRefreshing) return;
      const diff = e.touches[0].clientY - startYRef.current;
      if (diff <= 0) {
        setPullDistance(0);
        setIsPulling(false);
        return;
      }
      const resisted = Math.min(maxDistance, Math.pow(diff, 0.72));
      setPullDistance(resisted);
      setIsPulling(true);
      if (resisted > 5 && e.cancelable) e.preventDefault();
    };

    const onTouchEnd = async () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      const currentPull = pullDistance;

      if (currentPull >= threshold && !isRefreshing) {
        setIsRefreshing(true);
        setPullDistance(threshold);
        try {
          await onRefresh();
          await new Promise((r) => setTimeout(r, 800));
        } finally {
          setIsRefreshing(false);
          setPullDistance(0);
          setIsPulling(false);
        }
      } else {
        setPullDistance(0);
        setIsPulling(false);
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [onRefresh, pullDistance, isRefreshing, threshold, maxDistance]);

  return { pullDistance, isPulling, isRefreshing };
}

// ✅ هوک موقعیت‌یابی GPS
function useGeolocation({ onSuccess, onError, enabled = true }) {
  const watchIdRef = useRef(null);
  const retryTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  // از ref برای توابع استفاده می‌کنیم تا useEffect وابسته به آن‌ها نباشد
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  useEffect(() => {
    if (!enabled) return;

    isMountedRef.current = true;

    if (!navigator.geolocation) {
      onErrorRef.current?.({ code: 'NOT_SUPPORTED', message: 'مرورگر از موقعیت‌یابی پشتیبانی نمی‌کند' });
      return;
    }

    const handleSuccess = async (position) => {
      if (!isMountedRef.current) return;

      // پردازش موقعیت در consumer
      await onSuccessRef.current?.(position);

      // پس از ۳ ثانیه، watch را متوقف کن (شاید position بعدی دقیق‌تر باشد)
      if (isMountedRef.current && watchIdRef.current !== null) {
        setTimeout(() => {
          if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
          }
        }, 3000);
      }
    };

    const handleError = (error) => {
      if (!isMountedRef.current) return;
      onErrorRef.current?.(error);

      // تلاش مجدد پس از ۵ ثانیه برای POSITION_UNAVAILABLE یا TIMEOUT
      if (error.code === 2 || error.code === 3) {
        retryTimerRef.current = setTimeout(() => {
          if (!isMountedRef.current) return;
          navigator.geolocation.getCurrentPosition(
            handleSuccess,
            handleError,
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
          );
        }, 5000);
      }
    };

    // watchPosition برای بهبود تدریجی دقت
    watchIdRef.current = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );

    return () => {
      isMountedRef.current = false;
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [enabled]);
}

// ================================================================
// SECTION 6: WEATHER CONTEXT (تنها منبع حقیقت)
// ================================================================

const WeatherContext = createContext(null);
const useWeatherCtx = () => {
  const ctx = useContext(WeatherContext);
  if (!ctx) throw new Error('useWeatherCtx must be used within WeatherProvider');
  return ctx;
};

function WeatherProvider({ children }) {

  const { user } = useAuth();

  const userId = useMemo(() => {
    if (user?.id) return user.id;
    try {
      const saved = localStorage.getItem('user');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.id) return parsed.id;
      }
    } catch {}
    return 'guest';
  }, [user?.id]);

  const STORAGE_KEYS = useMemo(() => buildStorageKeys(userId), [userId]);



  // ─── cities ───
  const [cities, setCities] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(STORAGE_KEYS.CITIES) || 'null');
      return Array.isArray(s) && s.length ? s : [DEFAULT_CITY];
    } catch { return [DEFAULT_CITY]; }
  });

  const [primaryCityId, setPrimaryCityId] = useState(() => {
  try {
    return localStorage.getItem(STORAGE_KEYS.PRIMARY_CITY) || null;
  } catch { return null; }
});

const [activeIdx, setActiveIdx] = useState(() => {
  // ✅ اول شهر اصلی رو چک کن
  try {
    const pid = localStorage.getItem(STORAGE_KEYS.PRIMARY_CITY);
    if (pid) {
      const s = JSON.parse(localStorage.getItem(STORAGE_KEYS.CITIES) || 'null');
      if (Array.isArray(s)) {
        const idx = s.findIndex((c) => c.id === pid);
        if (idx >= 0) return idx;
      }
    }
  } catch {}
  const v = Number(localStorage.getItem(STORAGE_KEYS.ACTIVE_CITY) || 0);
  return Number.isFinite(v) ? v : 0;
});

  // ─── settings ───
const [settings, setSettings] = useState(() => {
  try {
    const s = JSON.parse(localStorage.getItem(STORAGE_KEYS.SETTINGS) || 'null');
    // ✅ کلید مشترک Navbar2 → darkMode
    const navDark = localStorage.getItem('navbar2-drawer-dark') === '1';
    return { ...DEFAULT_SETTINGS, ...(s || {}), darkMode: navDark };
  } catch { return { ...DEFAULT_SETTINGS }; }
});

  // ─── data per city (keyed by city.id) ───
  const [dataMap, setDataMap] = useState({});
  const [loadingMap, setLoadingMap] = useState({});
  const [errorMap, setErrorMap] = useState({});
  const inFlightRef = useRef({});

  // ─── persist ───
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.CITIES, JSON.stringify(cities));
      localStorage.setItem(STORAGE_KEYS.ACTIVE_CITY, String(activeIdx));
      localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
    } catch {}
  }, [cities, activeIdx, settings]);










  // ✅ سینک darkMode با navbar2-drawer-dark
useEffect(() => {
  try {
    localStorage.setItem('navbar2-drawer-dark', settings.darkMode ? '1' : '0');
  } catch {}
}, [settings.darkMode]);

useEffect(() => {
  const handleStorage = (e) => {
    if (e.key === 'navbar2-drawer-dark') {
      const isDark = e.newValue === '1';
      setSettings((prev) => (prev.darkMode !== isDark ? { ...prev, darkMode: isDark } : prev));
    }
  };
  window.addEventListener('storage', handleStorage);

  const interval = setInterval(() => {
    try {
      const isDark = localStorage.getItem('navbar2-drawer-dark') === '1';
      setSettings((prev) => (prev.darkMode !== isDark ? { ...prev, darkMode: isDark } : prev));
    } catch {}
  }, 500);

  return () => {
    window.removeEventListener('storage', handleStorage);
    clearInterval(interval);
  };
}, []);














// ✅ persist شهر اصلی
useEffect(() => {
  try {
    if (primaryCityId) {
      localStorage.setItem(STORAGE_KEYS.PRIMARY_CITY, primaryCityId);
    } else {
      localStorage.removeItem(STORAGE_KEYS.PRIMARY_CITY);
    }
  } catch {}
}, [primaryCityId]);

// ✅ GPS: موقعیت‌یابی خودکار
const gpsInitializedRef = useRef(false);
const [geoError, setGeoError] = useState(null);
const citiesRef = useRef(cities);

// نگه‌داشتن آخرین مقدار cities در ref (برای استفاده در callback بدون وابستگی)
useEffect(() => {
  citiesRef.current = cities;
}, [cities]);

const handleGpsSuccess = useCallback(async (position) => {
  if (gpsInitializedRef.current) return;

  // اگر کاربر از قبل شهر GPS دارد، کاری نکن
  if (citiesRef.current?.some((c) => c.isGPS)) {
    gpsInitializedRef.current = true;
    return;
  }

  const { latitude, longitude, accuracy } = position.coords;

  // نادیده گرفتن موقعیت‌های با دقت بسیار پایین (> ۱۰ کیلومتر)
  if (accuracy && accuracy > 10000) {
    console.warn(`GPS accuracy too low: ${accuracy}m, retrying...`);
    // gpsInitializedRef را true نکن → موقعیت بعدی watchPosition امتحان می‌شود
    return;
  }

  gpsInitializedRef.current = true;

  // دریافت نام شهر از مختصات
  const cityInfo = await reverseGeocode(latitude, longitude);

  const gpsCity = {
    id: `gps-${latitude.toFixed(4)}-${longitude.toFixed(4)}`,
    name: cityInfo?.name || 'موقعیت شما',
    country: cityInfo?.country || '—',
    admin1: cityInfo?.admin1 || '',
    latitude,
    longitude,
    timezone: 'auto', // ✅ Open-Meteo خودش timezone را حل می‌کند
    isGPS: true,
  };

  // افزودن شهر GPS به ابتدای لیست (جلوگیری از تکرار)
  setCities((prev) => {
    const filtered = prev.filter((c) => !c.isGPS);
    return [gpsCity, ...filtered];
  });

  // هدایت به شهر GPS
  setActiveIdx(0);
  setPrimaryCityId(gpsCity.id);
  setGeoError(null);
}, []);

const handleGpsError = useCallback((error) => {
  let msg = 'موقعیت‌یابی ناموفق بود';
  if (error.code === 1 || error.code === 'PERMISSION_DENIED') {
    msg = 'دسترسی به موقعیت مکانی رد شد. می‌توانید شهر را دستی جستجو کنید.';
  } else if (error.code === 2 || error.code === 'POSITION_UNAVAILABLE') {
    msg = 'موقعیت مکانی در دسترس نیست. لطفاً GPS را روشن کنید.';
  } else if (error.code === 3 || error.code === 'TIMEOUT') {
    msg = 'زمان دریافت موقعیت به پایان رسید. دوباره تلاش می‌شود...';
  }
  setGeoError(msg);
}, []);

// ✅ ✅ ✅ فراخوانی hook در سطح بالای کامپوننت (نه داخل useEffect)
const hasGpsCity = useMemo(() => cities.some((c) => c.isGPS), [cities]);

useGeolocation({
  onSuccess: handleGpsSuccess,
  onError: handleGpsError,
  enabled: !hasGpsCity,
});

// 🆕 درخواست دستی اجازه موقعیت مکانی (با User Gesture → پرامپت مرورگر باز می‌شود)
const requestLocationPermission = useCallback(() => {
  setGeoError(null);

  if (!navigator.geolocation) {
    setGeoError('مرورگر شما از موقعیت‌یابی پشتیبانی نمی‌کند');
    return;
  }

  // اجازه بده flow از ابتدا اجرا شود
  gpsInitializedRef.current = false;

  navigator.geolocation.getCurrentPosition(
    async (position) => {
      setGeoError(null); // خطا را حتی اگر شهر GPS از قبل وجود داشت، پاک کن
      await handleGpsSuccess(position);
    },
    (error) => {
      handleGpsError(error);
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );
}, [handleGpsSuccess, handleGpsError]);

// ─── clamp ───
useEffect(() => {
    if (activeIdx >= cities.length) {
      setActiveIdx(Math.max(0, cities.length - 1));
    }
  }, [cities.length, activeIdx]);

  // ─── fetch city (با cache ۳۰ دقیقه‌ای) ───
  const fetchCity = useCallback(async (city, { force = false } = {}) => {
    if (!city?.latitude || !city?.longitude) return;

    const cacheKey = `${city.latitude},${city.longitude}`;

    // cache check
// cache check
if (!force) {
  try {
    const cache = JSON.parse(localStorage.getItem(STORAGE_KEYS.CACHE) || '{}');
    const c = cache[cacheKey];
    const cacheValid =
      c &&
      Date.now() - c.ts < 30 * 60 * 1000 &&
      c.data?.current &&
      c.data?.hourly &&
      c.data?.daily;

    if (cacheValid) {
      setDataMap((m) => ({ ...m, [city.id]: c.data }));
      setErrorMap((m) => ({ ...m, [city.id]: null }));
      setLoadingMap((m) => ({ ...m, [city.id]: false }));
      return;
    }
  } catch {}
}

    // اگه درخواست همزمان در حال اجرا هست، نادیده بگیر
    if (inFlightRef.current[city.id] && !force) return;
    inFlightRef.current[city.id] = true;

    setLoadingMap((m) => ({ ...m, [city.id]: true }));
    setErrorMap((m) => ({ ...m, [city.id]: null }));

// ✅ برای شهر GPS از timezone=auto استفاده کن (Open-Meteo خودش حل می‌کند)
const tz = city.timezone === 'auto' ? 'auto' : encodeURIComponent(TIMEZONE);

const url =
  `${API_BASE}?latitude=${city.latitude}&longitude=${city.longitude}` +
  `&current=${CURRENT_PARAMS}` +
  `&hourly=${HOURLY_PARAMS}` +
  `&daily=${DAILY_PARAMS}` +
  `&timezone=${tz}` +
  `&forecast_days=16`;

try {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();

  // ✅ اگر داده ناقص بود، به‌عنوان خطا در نظر بگیر
  if (!json?.current || !json?.hourly || !json?.daily) {
    throw new Error('پاسخ نامعتبر از سرور');
  }

  setDataMap((m) => ({ ...m, [city.id]: json }));
  setErrorMap((m) => ({ ...m, [city.id]: null }));

  // save to cache
  try {
    const cache = JSON.parse(localStorage.getItem(STORAGE_KEYS.CACHE) || '{}');
    cache[cacheKey] = { ts: Date.now(), data: json };
    localStorage.setItem(STORAGE_KEYS.CACHE, JSON.stringify(cache));
  } catch {}
} catch (e) {
      let msg = e.message || 'خطای نامشخص';
      if (e.name === 'AbortError') msg = 'زمان درخواست به پایان رسید';
      setErrorMap((m) => ({ ...m, [city.id]: msg }));
    } finally {
      setLoadingMap((m) => ({ ...m, [city.id]: false }));
      delete inFlightRef.current[city.id];
    }
  }, []);

  // ─── refresh active city (برای pull-to-refresh) ───
  const refreshActiveCity = useCallback(async () => {
    const city = cities[activeIdx];
    if (!city) return;
    await fetchCity(city, { force: true });
  }, [cities, activeIdx, fetchCity]);

  // ─── add city ───
    // ─── add city ───
  const addCity = useCallback((city) => {
    setCities((prev) => {
      if (prev.find((c) => c.id === city.id)) return prev;
      return [...prev, city];
    });
    // ✅ دریافت فوری داده‌های شهر جدید (dedup داخل fetchCity)
    fetchCity(city);
  }, [fetchCity]);

  // ─── remove city ───
  const removeCity = useCallback((id) => {
    setCities((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((c) => c.id !== id);
      setActiveIdx((idx) => Math.min(idx, next.length - 1));
      return next;
    });
  }, []);

  // ✅ تعیین شهر اصلی
const setPrimaryCity = useCallback((cityId) => {
  setPrimaryCityId(cityId);
}, []);

  const value = useMemo(() => ({
  cities, activeIdx, setActiveIdx,
  settings, setSettings,
  dataMap, loadingMap, errorMap,
  fetchCity, refreshActiveCity,
  addCity, removeCity, primaryCityId, setPrimaryCity,
  geoError, // ✅ اضافه شد
  requestLocationPermission, // 🆕
}), [cities, activeIdx, settings, dataMap, loadingMap, errorMap, fetchCity, refreshActiveCity, addCity, removeCity, primaryCityId, setPrimaryCity, geoError, requestLocationPermission]);

  return <WeatherContext.Provider value={value}>{children}</WeatherContext.Provider>;
}

// ================================================================
// SECTION 7: MAIN PAGE
// ================================================================

export default function WeatherPage() {
  return (
    <WeatherProvider>
      <WeatherPageInner />
    </WeatherProvider>
  );
}

function WeatherPageInner() {
const {
  cities, activeIdx, setActiveIdx,
  settings, setSettings,
  dataMap, loadingMap, errorMap,
  fetchCity, refreshActiveCity,
  addCity, removeCity,
  primaryCityId, setPrimaryCity,
  geoError,
  requestLocationPermission, // 🆕
} = useWeatherCtx();

  const [showSearch, setShowSearch] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHourlyModal, setShowHourlyModal] = useState(false);
  const [showDailyModal, setShowDailyModal] = useState(false);

  // ✅ state برای بستن بنر خطای GPS
  const [geoBannerDismissed, setGeoBannerDismissed] = useState(false);




    const swiperRef = useRef(null);
  const prevCityIdsRef = useRef([]);
  const isProgrammaticScrollRef = useRef(false);
  const scrollEndTimerRef = useRef(null);
  const programmaticResetTimerRef = useRef(null);

  // ✅ ۱. وقتی activeIdx عوض می‌شه، شهر هدف رو با scrollIntoView وسط/ابتدای صفحه بیار
  //    این روش به scrollLeft کار نداره → کاملاً RTL-safe
  useEffect(() => {
  const el = swiperRef.current;
  if (!el) return;
  const target = el.children?.[activeIdx];
  if (!target) return;

  isProgrammaticScrollRef.current = true;

  // ✅ فقط افقی حرکت کن — بدون دست‌زدن به اسکرول عمودی
  const elRect = el.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const deltaX = targetRect.left - elRect.left;

  el.scrollBy({ left: deltaX, behavior: 'smooth' });

  if (programmaticResetTimerRef.current) clearTimeout(programmaticResetTimerRef.current);
  programmaticResetTimerRef.current = setTimeout(() => {
    isProgrammaticScrollRef.current = false;
  }, 600);
}, [activeIdx, cities.length]);

  // ✅ ۲. تشخیص اسکرول دستی با موقعیت واقعی هر عنصر (RTL-safe)
  const onSwiperScroll = useCallback(() => {
    if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = setTimeout(() => {
      if (isProgrammaticScrollRef.current) return;
      const el = swiperRef.current;
      if (!el) return;
      const children = el.children;
      if (!children || children.length === 0) return;

      // مرکز کانتینر
      const elRect = el.getBoundingClientRect();
      const containerCenter = elRect.left + elRect.width / 2;

      // نزدیک‌ترین child به مرکز
      let closestIdx = 0;
      let closestDist = Infinity;
      for (let i = 0; i < children.length; i++) {
        const r = children[i].getBoundingClientRect();
        const childCenter = r.left + r.width / 2;
        const d = Math.abs(childCenter - containerCenter);
        if (d < closestDist) {
          closestDist = d;
          closestIdx = i;
        }
      }

      if (closestIdx >= 0 && closestIdx < cities.length) {
        setActiveIdx((prev) => (prev !== closestIdx ? closestIdx : prev));
      }
    }, 120);
  }, [cities.length, setActiveIdx]);

  // ✅ ۳. goToCity فقط state رو عوض می‌کنه
  const goToCity = useCallback((idx) => {
    if (idx < 0 || idx >= cities.length) return;
    setActiveIdx(idx);
  }, [cities.length, setActiveIdx]);

  // ✅ پاکسازی تایمرها
  useEffect(() => {
    return () => {
      if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
      if (programmaticResetTimerRef.current) clearTimeout(programmaticResetTimerRef.current);
    };
  }, []);




  // ✅ دریافت داده‌های تمام شهرها به‌صورت موازی — بلافاصله
  useEffect(() => {
    cities.forEach((city) => fetchCity(city));
  }, [cities, fetchCity]);

  // ✅ تشخیص شهر جدید → هدایت خودکار به آن
  useEffect(() => {
    const prev = prevCityIdsRef.current;
    const curr = cities.map((c) => c.id);

    // فقط وقتی شهر جدیدی اضافه شده (نه در حالت اولیه، نه در حذف)
    if (prev.length > 0 && curr.length > prev.length) {
      const added = curr.find((id) => !prev.includes(id));
      if (added) {
        const idx = curr.indexOf(added);
        // صبر کوتاه تا swiper mount شود و clientWidth داشته باشد
        setTimeout(() => goToCity(idx), 80);
      }
    }
    prevCityIdsRef.current = curr;
  }, [cities, goToCity]);

  const activeCity = cities[activeIdx];

  const { pullDistance, isPulling, isRefreshing } = usePullToRefresh(refreshActiveCity);

  const pullProgress = Math.min(1, pullDistance / 80);
  const showPullIndicator = isPulling || isRefreshing;

  return (
    <div className={`weather-page ${settings.darkMode ? 'dark' : ''}`}>
      <WeatherHeader
        city={activeCity}
        onSearch={() => setShowSearch(true)}
        onSettings={() => setShowSettings(true)}
      />

      {/* ✅ بنر خطای GPS */}
      {geoError && !geoBannerDismissed && (
        <div className="wp-geo-error-banner">
          <span className="wp-geo-error-icon">📍</span>
          <span className="wp-geo-error-text">{geoError}</span>
          <button
            className="wp-geo-request-btn"
            onClick={() => {
              setGeoBannerDismissed(false);
              requestLocationPermission();
            }}
            title="درخواست اجازه دسترسی به موقعیت مکانی"
          >
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none">
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2.4" />
              <path
                d="M12 2v3M12 19v3M2 12h3M19 12h3"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
            <span>درخواست اجازه</span>
          </button>
          <button
            className="wp-geo-error-close"
            onClick={() => setGeoBannerDismissed(true)}
            aria-label="بستن"
          >
            ✕
          </button>
        </div>
      )}

      {/* ✅ شاخص Pull-to-Refresh با طراحی دایره‌ای و فلش چرخان */}
<div
  className={`wp-ptr ${showPullIndicator ? 'visible' : ''} ${isRefreshing ? 'refreshing' : ''}`}
  style={{
    '--pull-progress': pullProgress,
    transform: `translateX(-50%) translateY(${Math.min(pullDistance, 90)}px)`,
  }}
>
  <div className="wp-ptr-circle">
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" className="wp-ptr-arrow">
      {/* مسیر فلش رو به پایین */}
      <path
        d="M12 5 V19 M6 13 L12 19 L18 13"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
  <span className="wp-ptr-text">
    {isRefreshing ? 'در حال بروزرسانی...' : pullProgress >= 1 ? 'رها کنید' : 'بکشید پایین'}
  </span>
</div>

      <div
  className="wp-swiper"
  ref={swiperRef}
  onScroll={onSwiperScroll}
>
        {cities.map((city) => (
          <CityView
            key={city.id}
            city={city}
            settings={settings}
            data={dataMap[city.id]}
            loading={loadingMap[city.id]}
            error={errorMap[city.id]}
            onRetry={() => fetchCity(city, { force: true })}
            onOpenHourly={() => setShowHourlyModal(true)}
            onOpenDaily={() => setShowDailyModal(true)}
          />
        ))}
      </div>

      {cities.length > 1 && (
        <div className="wp-dots">
          {cities.map((c, i) => (
            <button
              key={c.id}
              className={`wp-dot ${i === activeIdx ? 'active' : ''}`}
              onClick={() => goToCity(i)}
              aria-label={c.name}
            />
          ))}
        </div>
      )}

      {showSearch && (
        <SearchModal
          cities={cities}
          onAdd={addCity}
          onClose={() => setShowSearch(false)}
        />
      )}

            {showSettings && (
        <SettingsModal
          settings={settings}
          onChange={setSettings}
          cities={cities}
          activeIdx={activeIdx}
          onRemoveCity={removeCity}
          primaryCityId={primaryCityId}
          onSetPrimaryCity={setPrimaryCity}
          onClose={() => setShowSettings(false)}
        />
      )}

      {showHourlyModal && activeCity && dataMap[activeCity.id] && (
        <HourlyModal
          city={activeCity}
          data={dataMap[activeCity.id]}
          settings={settings}
          onClose={() => setShowHourlyModal(false)}
        />
      )}
      {showDailyModal && activeCity && dataMap[activeCity.id] && (
        <DailyModal
          city={activeCity}
          data={dataMap[activeCity.id]}
          settings={settings}
          onClose={() => setShowDailyModal(false)}
        />
      )}
    </div>
  );
}

// ================================================================
// SECTION 8: HEADER
// ================================================================

function WeatherHeader({ city, onSearch, onSettings }) {
  const navigate = useNavigate();                 // 🆕
  const { user } = useAuth();                     // 🆕

  const handleBack = () => {                      // 🆕 (کل تابع)
    // آیا کاربر از داخل اپ اومده یا مستقیم این صفحه رو باز کرده؟
    const historyIdx = window.history.state?.idx ?? 0;

    if (historyIdx > 0) {
      navigate(-1);                               // برگشت واقعی SPA
    } else {
      // ورود مستقیم → بر اساس نقش کاربر برو صفحه اصلی
      const roleRoutes = {
        student: '/StudentHome',
        teacher: '/TeacherHome',
        parent:  '/ParentHome',
        admin:   '/AdminHome',
      };
      const target = roleRoutes[user?.role] || '/TeacherHome';
      navigate(target, { replace: true });        // replace تا دکمه back به WeatherPage برنگرده
    }
  };

  return (
    <header className="wp-header">
      <button className="wp-hdr-btn" onClick={onSearch} aria-label="جستجو">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

<div className="wp-hdr-city-wrap">
  <h1 className="wp-hdr-city">
    {city?.name || 'تهران'}
    {city?.isGPS && (
      <span className="wp-hdr-gps-badge" title="موقعیت شما (GPS)">
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none">
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2.5" />
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </span>
    )}
  </h1>
  {city?.country && <span className="wp-hdr-country">{city.country}</span>}
</div>

      <button className="wp-hdr-btn" onClick={onSettings} aria-label="تنظیمات">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      </button>

      <button className="wp-hdr-btn" onClick={handleBack} aria-label="بازگشت">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </header>
  );
}

// ================================================================
// SECTION 9: CITY VIEW
// ================================================================

function CityView({ city, settings, data, loading, error, onRetry, onOpenHourly, onOpenDaily }) {
  // ✅ تا وقتی data کامل نداریم، همیشه لودینگ (یا خطا) نشون بده — نه بلانک
  if (!data || !data.current || !data.daily || !data.hourly) {
    if (error) {
      return (
        <section className="wp-city-view">
          <div className="wp-error">
            <span>⚠️</span>
            <p>خطا در دریافت اطلاعات آب‌وهوا</p>
            <small>{error}</small>
            <button className="wp-retry-btn" onClick={onRetry}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                <path d="M3 12a9 9 0 0 1 15-6.7L21 8M3 12a9 9 0 0 0 15 6.7L21 16M21 3v5h-5M3 21v-5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>تلاش مجدد</span>
            </button>
          </div>
        </section>
      );
    }

    return (
      <section className="wp-city-view">
        <div className="wp-loading">
          <div className="wp-spinner" />
          <p>در حال دریافت اطلاعات {city.name}...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="wp-city-view">
      <HeroSection city={city} data={data} settings={settings} />
      <HourlyCard data={data} settings={settings} cityId={city.id} onOpen={onOpenHourly} />
      <DailyCard data={data} settings={settings} onOpen={onOpenDaily} />
      <DetailGrid data={data} settings={settings} />
      <SunMoonCard data={data} settings={settings} cityId={city.id} />
      <LifeForecast data={data} settings={settings} />
    </section>
  );
}

// ================================================================
// SECTION 10: HERO
// ================================================================

function HeroSection({ city, data, settings }) {
  const cur = data.current || {};
  const wmo = getWMO(cur.weather_code);
  const isDay = cur.is_day === 1;
  const temp = convertTemp(cur.temperature_2m, settings.tempUnit);
  const feels = convertTemp(cur.apparent_temperature, settings.tempUnit);
  const maxT = convertTemp(data.daily.temperature_2m_max?.[0], settings.tempUnit);
  const minT = convertTemp(data.daily.temperature_2m_min?.[0], settings.tempUnit);

  return (
    <section className={`wp-hero wp-grad-${wmo.grad} ${isDay ? 'is-day' : 'is-night'}`}>
      <div className="wp-hero-bg" aria-hidden="true">
        <span className="wp-blob wp-blob-1" />
        <span className="wp-blob wp-blob-2" />
        <span className="wp-blob wp-blob-3" />
      </div>

      <div className="wp-hero-inner">
        <div className="wp-hero-icon">
          <WeatherIcon type={wmo.icon} isDay={isDay} size={96} />
        </div>
        <div className="wp-hero-temp">
          {toFa(temp)}{tempSymbol(settings.tempUnit)[1]}
        </div>
        <div className="wp-hero-cond">{wmo.label}</div>
        <div className="wp-hero-feels">
          حس واقعی: {toFa(feels)}{tempSymbol(settings.tempUnit)[1]}
        </div>
        <div className="wp-hero-meta">
          <span>حداکثر {toFa(maxT)}°</span>
          <span className="sep">•</span>
          <span>حداقل {toFa(minT)}°</span>
        </div>
      <div className="wp-hero-date">
        {formatDateFull(cur.time || '—')}
      </div>
        {/*<div className="wp-hero-time">
          آخرین بروزرسانی: {formatTime(data.current.time, settings.timeFormat)}
        </div>*/}
      </div>
    </section>
  );
}

// ================================================================
// SECTION 11: HOURLY CARD (Sparkline هم‌تراز)
// ================================================================

function HourlyCard({ data, settings, cityId, onOpen }) {
  const currentHourIdx = useMemo(() => findCurrentHourIndex(data.hourly?.time), [data.hourly?.time]);

  // ۲۴ ساعت از الان به بعد
  const hourIndices = useMemo(() => {
    const out = [];
    for (let i = currentHourIdx; i < data.hourly.time.length && out.length < 24; i++) {
      out.push(i);
    }
    return out;
  }, [data.hourly.time, currentHourIdx]);



  // ✅ Sparkline با preserveAspectRatio="none" روی عرض واقعی ستون‌ها
  const { sparkPath, sparkWidth, points } = useMemo(() => {
    if (hourIndices.length < 2) return { sparkPath: '', sparkWidth: 0, points: [] };
    const temps = hourIndices.map((i) => data.hourly.temperature_2m[i]);
    const min = Math.min(...temps);
    const max = Math.max(...temps);
    const range = max - min || 1;
    const H = 26;
    const COL_W = 66;
    const W = hourIndices.length * COL_W - 8;
    const pts = temps.map((t, i) => ({
  x: i * COL_W + 29,
  y: H - ((t - min) / range) * (H - 6) - 3,
}));
    const path = pts.map((p, i) =>
      `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`
    ).join(' ');
    return { sparkPath: path, sparkWidth: W, points: pts };
  }, [data.hourly.temperature_2m, hourIndices]);




  const gradId = `hrGrad-${cityId}`;

  return (
    <section className="wp-card wp-hourly">
      <div className="wp-card-head">
        <span className="wp-card-title">⏰ پیش‌بینی ساعتی</span>
        <span className="wp-card-sub">۲۴ ساعت آینده</span>
      </div>



      <div className="wp-hr-scroll">
  <div className="wp-hr-cols">
    {/* ✅ SVG داخل cols → هم‌مختصات با ستون‌ها */}
    <svg
  className="wp-hr-spark"
  width={sparkWidth}
  height={26}
  viewBox={`0 0 ${sparkWidth} 26`}
  preserveAspectRatio="none"
  aria-hidden="true"
>
      <defs>
        <linearGradient id={gradId} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#60a5fa" />
        </linearGradient>
      </defs>

      {/* خط نمودار */}
      <path
        d={sparkPath}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* دایره‌های توخالی روی هر نقطه */}
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r="2.6"
          fill="var(--wp-bg-card-solid)"
          stroke={`url(#${gradId})`}
          strokeWidth="1.6"
        />
      ))}
    </svg>

    {hourIndices.map((idx, i) => {
      const code = data.hourly.weather_code[idx];
      const isDay = data.hourly.is_day[idx] === 1;
      const wmo = getWMO(code);
      const temp = convertTemp(data.hourly.temperature_2m[idx], settings.tempUnit);
      const rain = safeNum(data.hourly.precipitation_probability?.[idx], 0);
      const timeLabel = i === 0 ? 'الان' : formatTime(data.hourly.time[idx], settings.timeFormat);
      return (
        <div key={idx} className="wp-hr-col">
          <span className="wp-hr-time">{timeLabel}</span>
          <span className="wp-hr-ico">
            <WeatherIcon type={wmo.icon} isDay={isDay} size={28} />
          </span>
          <span className="wp-hr-temp">{toFa(temp)}°</span>
          <span className="wp-hr-rain">{toFa(rain)}٪</span>
        </div>
      );
    })}
  </div>
</div>



      <button className="wp-card-cta" onClick={onOpen}>
        <span>نمایش ۴۸ ساعته</span>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </section>
  );
}

// ================================================================
// SECTION 12: DAILY CARD
// ================================================================

function DailyCard({ data, settings, onOpen }) {
  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < Math.min(7, data.daily.time.length); i++) out.push(i);
    return out;
  }, [data]);

  return (
    <section className="wp-card wp-daily">
      <div className="wp-card-head">
        <span className="wp-card-title">📅 پیش‌بینی روزانه</span>
        <span className="wp-card-sub">۷ روز آینده</span>
      </div>

      <ul className="wp-dy-list">
        {days.map((i) => {
          const code = data.daily.weather_code[i];
          const wmo = getWMO(code);
          const maxT = convertTemp(data.daily.temperature_2m_max[i], settings.tempUnit);
          const minT = convertTemp(data.daily.temperature_2m_min[i], settings.tempUnit);
          const rain = safeNum(data.daily.precipitation_probability_max?.[i], 0);
          const isToday = i === 0;
          const dayName = isToday ? 'امروز' : formatDayName(data.daily.time[i]);
          return (
            <li key={i} className={`wp-dy-row ${isToday ? 'today' : ''}`}>
              <span className="wp-dy-name">{dayName}</span>
              <span className="wp-dy-ico">
                <WeatherIcon type={wmo.icon} isDay={true} size={26} />
              </span>
              <span className="wp-dy-rain">{toFa(rain)}٪</span>
              <span className="wp-dy-temps">
                <span className="wp-dy-min">{toFa(minT)}°</span>
                <span className="wp-dy-max">{toFa(maxT)}°</span>
              </span>
            </li>
          );
        })}
      </ul>

      <button className="wp-card-cta" onClick={onOpen}>
        <span>نمایش ۱۶ روزه</span>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </section>
  );
}

// ================================================================
// SECTION 13: DETAIL GRID (با ایندکس ساعت فعلی)
// ================================================================

function DetailGrid({ data, settings }) {
  const cur = data.current || {};
  const currentHourIdx = useMemo(() => findCurrentHourIndex(data.hourly?.time), [data.hourly?.time]);

  const uv = safeNum(data.hourly.uv_index?.[currentHourIdx], 0);
  const visibility = safeNum(data.hourly.visibility?.[currentHourIdx], 0);
  const dew = safeNum(data.hourly.dew_point_2m?.[currentHourIdx], 0);
  const uvInfo = uvDesc(uv);

  const items = [
    {
      key: 'humidity', icon: '💧', label: 'رطوبت',
      value: `${toFa(Math.round(safeNum(cur.relative_humidity_2m)))}٪`,
      sub: humidityDesc(safeNum(cur.relative_humidity_2m)), tone: 'blue',
    },
    {
      key: 'wind', icon: '🌬️', label: 'باد',
      value: `${toFa(convertWind(cur.wind_speed_10m, settings.windUnit))} ${windSymbol(settings.windUnit)}`,
      sub: `${windDesc(safeNum(cur.wind_speed_10m))} • ${windDir(cur.wind_direction_10m)}`,
      tone: 'teal',
    },
    {
      key: 'uv', icon: '☀️', label: 'UV',
      value: `${toFa(Math.round(uv))}`,
      sub: uvInfo.text, tone: 'orange',
    },
    {
      key: 'pressure', icon: '🔽', label: 'فشار',
      value: `${toFa(Math.round(safeNum(cur.surface_pressure)))}`,
      sub: pressureDesc(safeNum(cur.surface_pressure)), tone: 'purple',
    },
    {
      key: 'visibility', icon: '👁️', label: 'دید',
      value: `${toFa(Math.round(visibility / 1000))} km`,
      sub: visibilityDesc(visibility), tone: 'indigo',
    },
    {
      key: 'dew', icon: '🌡️', label: 'نقطه شبنم',
      value: `${toFa(Math.round(dew))}°`,
      sub: dewPointDesc(dew), tone: 'pink',
    },
  ];

  return (
    <section className="wp-detail-grid">
      {items.map((it) => (
        <div key={it.key} className={`wp-detail wp-detail-${it.tone}`}>
          <div className="wp-detail-ico">{it.icon}</div>
          <div className="wp-detail-body">
            <span className="wp-detail-label">{it.label}</span>
            <span className="wp-detail-value">{it.value}</span>
            <span className="wp-detail-sub">{it.sub}</span>
          </div>
        </div>
      ))}
    </section>
  );
}

// ================================================================
// SECTION 14: SUN & MOON
// ================================================================

function SunMoonCard({ data, settings, cityId }) {
  const sunrise = data.daily.sunrise?.[0];
  const sunset = data.daily.sunset?.[0];
  const sr = formatTime(sunrise, settings.timeFormat);
  const ss = formatTime(sunset, settings.timeFormat);

  let hrs = 0, mins = 0;
  if (sunrise && sunset) {
    const diffMs = new Date(sunset).getTime() - new Date(sunrise).getTime();
    if (diffMs > 0) {
      hrs = Math.floor(diffMs / 3600000);
      mins = Math.floor((diffMs % 3600000) / 60000);
    }
  }

  const gradId = `arcGrad-${cityId}`;

  return (
    <section className="wp-card wp-sunmoon">
      <div className="wp-card-head">
        <span className="wp-card-title">🌅 طلوع و غروب</span>
      </div>
      <div className="wp-sunmoon-inner">
        <div className="wp-sunmoon-arc">
          <svg viewBox="0 0 200 90" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id={gradId} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#fbbf24" />
                <stop offset="100%" stopColor="#f472b6" />
              </linearGradient>
            </defs>
            <path d="M10 80 Q100 -10 190 80" fill="none" stroke={`url(#${gradId})`} strokeWidth="2" strokeDasharray="4 4" />
            <circle cx="10" cy="80" r="5" fill="#fbbf24" />
            <circle cx="190" cy="80" r="5" fill="#f472b6" />
            <circle cx="100" cy="12" r="7" fill="#fbbf24">
              <animate attributeName="r" values="7;9;7" dur="2.5s" repeatCount="indefinite" />
            </circle>
          </svg>
        </div>
        <div className="wp-sunmoon-info">
          <div className="wp-sunmoon-row">
            <span className="wp-sm-ico">🌅</span>
            <span className="wp-sm-label">طلوع</span>
            <span className="wp-sm-val">{sr}</span>
          </div>
          <div className="wp-sunmoon-row">
            <span className="wp-sm-ico">🌇</span>
            <span className="wp-sm-label">غروب</span>
            <span className="wp-sm-val">{ss}</span>
          </div>
          <div className="wp-sunmoon-row">
            <span className="wp-sm-ico">⏱️</span>
            <span className="wp-sm-label">مدت روز</span>
            <span className="wp-sm-val">{toFa(hrs)} ساعت {toFa(mins)} دقیقه</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ================================================================
// SECTION 15: LIFE FORECAST
// ================================================================

function LifeForecast({ data, settings }) {
  const cur = data.current || {};
  const temp = safeNum(cur.temperature_2m, 20);
  const wind = safeNum(cur.wind_speed_10m, 0);
  const currentHourIdx = useMemo(() => findCurrentHourIndex(data.hourly?.time), [data.hourly?.time]);
  const rain = safeNum(data.hourly.precipitation_probability?.[currentHourIdx], 0);

  const assess = (tempMin, tempMax, windMax, rainMax) => {
    let score = 100;
    if (temp < tempMin || temp > tempMax) score -= 40;
    if (wind > windMax) score -= 25;
    if (rain > rainMax) score -= 30;
    if (score >= 75) return { emoji: '✅', label: 'عالی', tone: 'good' };
    if (score >= 50) return { emoji: '⚠️', label: 'متوسط', tone: 'mid' };
    return { emoji: '❌', label: 'نامناسب', tone: 'bad' };
  };

  const activities = [
    { key: 'run', icon: '🏃', name: 'دویدن', ...assess(5, 28, 25, 40) },
    { key: 'bike', icon: '🚴', name: 'دوچرخه‌سواری', ...assess(5, 30, 30, 40) },
    { key: 'walk', icon: '🚶', name: 'پیاده‌روی', ...assess(0, 35, 35, 50) },
    { key: 'hike', icon: '🥾', name: 'کوهنوردی', ...assess(5, 25, 30, 30) },
    { key: 'garden', icon: '🌱', name: 'باغبانی', ...assess(8, 30, 20, 20) },
    { key: 'camp', icon: '🏕️', name: 'کمپینگ', ...assess(5, 28, 25, 20) },
  ];

  return (
    <section className="wp-card wp-life">
      <div className="wp-card-head">
        <span className="wp-card-title">🏃 پیش‌بینی فعالیت</span>
        <span className="wp-card-sub">۳ ساعت آینده</span>
      </div>
      <div className="wp-life-grid">
        {activities.map((a) => (
          <div key={a.key} className={`wp-life-item tone-${a.tone}`}>
            <span className="wp-life-ico">{a.icon}</span>
            <span className="wp-life-name">{a.name}</span>
            <span className="wp-life-status">{a.emoji} {a.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ================================================================
// SECTION 16: HOURLY MODAL (48h)
// ================================================================

function HourlyModal({ city, data, settings, onClose }) {
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const cur = data.current || {};
  const wmo = getWMO(cur.weather_code);
  const isDay = cur.is_day === 1;
  const currentHourIdx = useMemo(() => findCurrentHourIndex(data.hourly?.time), [data.hourly?.time]);

  const items = useMemo(() => {
    const out = [];
    for (let i = currentHourIdx; i < data.hourly.time.length && out.length < 48; i++) {
      out.push(i);
    }
    return out;
  }, [data.hourly.time, currentHourIdx]);

  return (
    <div className="wp-modal-overlay" onClick={onClose}>
      <div className="wp-modal" onClick={(e) => e.stopPropagation()}>
  <div className={`wp-modal-hero wp-grad-${wmo.grad}`}>
          <button className="wp-modal-back" onClick={onClose} aria-label="بازگشت">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="wp-modal-hero-blobs">
            <span className="wp-blob wp-blob-1" />
            <span className="wp-blob wp-blob-2" />
          </div>
          <div className="wp-modal-hero-content">
            <div className="wp-modal-hero-icon">
              <WeatherIcon type={wmo.icon} isDay={isDay} size={72} />
            </div>
            <div className="wp-modal-hero-temp">
              {toFa(convertTemp(cur.temperature_2m, settings.tempUnit))}°
            </div>
            <div className="wp-modal-hero-cond">{wmo.label}</div>
            <div className="wp-modal-hero-meta">
              <span>حس واقعی {toFa(convertTemp(cur.apparent_temperature, settings.tempUnit))}°</span>
              <span className="sep">•</span>
              <span>{city.name}</span>
            </div>
          </div>
        </div>

        <div className="wp-modal-body">
          <h3 className="wp-modal-section">۴۸ ساعت آینده</h3>
          <div className="wp-modal-list">
            {items.map((idx, i) => {
              const t = data.hourly.time[idx];
              const code = data.hourly.weather_code[idx];
              const isDayH = data.hourly.is_day[idx] === 1;
              const wmoH = getWMO(code);
              const tempH = convertTemp(data.hourly.temperature_2m[idx], settings.tempUnit);
              const rainH = safeNum(data.hourly.precipitation_probability?.[idx], 0);
              const open = openIdx === idx;
              const thisDate = t.slice(0, 10);
              const prevIdx = i > 0 ? items[i - 1] : null;
              const prevDate = prevIdx != null ? data.hourly.time[prevIdx].slice(0, 10) : null;
              const isNewDay = thisDate !== prevDate;

              
return (
  <React.Fragment key={idx}>
    {isNewDay && (
      <div className="wp-mrow-day">
        {i === 0 ? 'امروز' : formatDayName(t)}
      </div>
    )}
    <div className={`wp-mrow ${open ? 'open' : ''}`}>
      <button className="wp-mrow-head" onClick={() => setOpenIdx(open ? null : idx)}>
        <span className="wp-mrow-time">
          {i === 0 ? 'الان' : formatTime(t, settings.timeFormat)}
        </span>
        <span className="wp-mrow-ico">
          <WeatherIcon type={wmoH.icon} isDay={isDayH} size={32} />
        </span>
        <span className="wp-mrow-temp">{toFa(tempH)}°</span>
        <span className="wp-mrow-rain">{toFa(rainH)}٪</span>
        <span className="wp-mrow-chev">
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" overflow="visible">
    <path
      d={open ? 'M6 15 L12 9 L18 15' : 'M6 9 L12 15 L18 9'}
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
</span>
      </button>
      {open && (
        <div className="wp-mrow-details">
          <DetailRow icon="💧" label="رطوبت" value={`${toFa(Math.round(safeNum(data.hourly.relative_humidity_2m[idx])))}٪`} />
          <DetailRow icon="🌡️" label="حس واقعی" value={`${toFa(convertTemp(data.hourly.apparent_temperature[idx], settings.tempUnit))}°`} />
          <DetailRow icon="🌬️" label="باد" value={`${toFa(convertWind(data.hourly.wind_speed_10m[idx], settings.windUnit))} ${windSymbol(settings.windUnit)} · ${windDir(data.hourly.wind_direction_10m[idx])}`} />
          <DetailRow icon="☀️" label="UV" value={`${toFa(Math.round(safeNum(data.hourly.uv_index[idx])))} · ${uvDesc(safeNum(data.hourly.uv_index[idx])).text}`} />
          <DetailRow icon="👁️" label="دید" value={`${toFa(Math.round(safeNum(data.hourly.visibility[idx]) / 1000))} km`} />
          <DetailRow icon="☁️" label="ابرناکی" value={`${toFa(Math.round(safeNum(data.hourly.cloud_cover[idx])))}٪`} />
          <DetailRow icon="🌡️" label="نقطه شبنم" value={`${toFa(Math.round(safeNum(data.hourly.dew_point_2m[idx])))}°`} />
        </div>
      )}
    </div>
  </React.Fragment>
);
            })}
          </div>

          <button className="wp-modal-close" onClick={onClose}>بستن</button>
        </div>
      </div>
    </div>
  );
}

// ================================================================
// SECTION 17: DAILY MODAL (16d)
// ================================================================

function DailyModal({ city, data, settings, onClose }) {
  const [openIdx, setOpenIdx] = useState(0);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const avg = useMemo(() => {
    const temps = data.daily.temperature_2m_max.slice(0, 7);
    return temps.reduce((a, b) => a + b, 0) / temps.length;
  }, [data.daily.temperature_2m_max]);

  const cur = data.current || {};
  const wmo = getWMO(cur.weather_code);
  const isDay = cur.is_day === 1;

  return (
    <div className="wp-modal-overlay" onClick={onClose}>
      <div className="wp-modal" onClick={(e) => e.stopPropagation()}>
  <div className={`wp-modal-hero wp-grad-${wmo.grad}`}>
          <button className="wp-modal-back" onClick={onClose} aria-label="بازگشت">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="wp-modal-hero-blobs">
            <span className="wp-blob wp-blob-1" />
            <span className="wp-blob wp-blob-2" />
          </div>
          <div className="wp-modal-hero-content">
            <div className="wp-modal-hero-icon">
              <WeatherIcon type={wmo.icon} isDay={isDay} size={72} />
            </div>
            <div className="wp-modal-hero-temp">
              {toFa(convertTemp(avg, settings.tempUnit))}°
            </div>
            <div className="wp-modal-hero-cond">میانگین هفته</div>
            <div className="wp-modal-hero-meta">
              <span>{city.name}</span>
              <span className="sep">•</span>
              <span>{toFa(data.daily.time.length)} روز</span>
            </div>
          </div>
        </div>

        <div className="wp-modal-body">
          <h3 className="wp-modal-section">پیش‌بینی ۱۶ روزه</h3>
          <div className="wp-modal-list">
            {data.daily.time.map((dateStr, i) => {
              const code = data.daily.weather_code[i];
              const wmoD = getWMO(code);
              const maxT = convertTemp(data.daily.temperature_2m_max[i], settings.tempUnit);
              const minT = convertTemp(data.daily.temperature_2m_min[i], settings.tempUnit);
              const rain = safeNum(data.daily.precipitation_probability_max?.[i], 0);
              const open = openIdx === i;
              const isToday = i === 0;
              const label = isToday ? 'امروز' : formatDayShort(dateStr);
              const weekday = isToday ? 'امروز' : formatDayName(dateStr);

              return (
                <div key={i} className={`wp-mrow ${open ? 'open' : ''} ${isToday ? 'today' : ''}`}>
                  <button className="wp-mrow-head" onClick={() => setOpenIdx(open ? null : i)}>
                    <span className="wp-mrow-time wp-mrow-time-daily">
  <span className="wp-mrow-wd">{weekday}</span>
  <span className="wp-mrow-date">{isToday ? 'امروز' : formatDayShort(dateStr)}</span>
</span>
                    <span className="wp-mrow-ico">
                      <WeatherIcon type={wmoD.icon} isDay={true} size={32} />
                    </span>
                    <span className="wp-mrow-temp">{toFa(maxT)}°</span>
                    <span className="wp-mrow-rain">{toFa(rain)}٪</span>
                    <span className="wp-mrow-chev">
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" overflow="visible">
    <path
      d={open ? 'M6 15 L12 9 L18 15' : 'M6 9 L12 15 L18 9'}
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
</span>
                  </button>
                  {open && (
                    <div className="wp-mrow-details">
                      <DetailRow icon="📅" label="تاریخ" value={formatDateFull(dateStr)} />
                      <DetailRow icon="🌡️" label="حداکثر / حداقل" value={`${toFa(maxT)}° / ${toFa(minT)}°`} />
                      <DetailRow icon="💧" label="احتمال بارش" value={`${toFa(rain)}٪`} />
                      <DetailRow icon="🌬️" label="باد" value={`${toFa(convertWind(data.daily.wind_speed_10m_max[i], settings.windUnit))} ${windSymbol(settings.windUnit)} · ${windDir(data.daily.wind_direction_10m_dominant[i])}`} />
                      <DetailRow icon="☀️" label="UV" value={`${toFa(Math.round(safeNum(data.daily.uv_index_max[i])))} · ${uvDesc(safeNum(data.daily.uv_index_max[i])).text}`} />
                      <DetailRow icon="🌅" label="طلوع" value={formatTime(data.daily.sunrise[i], settings.timeFormat)} />
                      <DetailRow icon="🌇" label="غروب" value={formatTime(data.daily.sunset[i], settings.timeFormat)} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button className="wp-modal-close" onClick={onClose}>بستن</button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon, label, value }) {
  return (
    <div className="wp-drow">
      <span className="wp-drow-ico">{icon}</span>
      <span className="wp-drow-label">{label}</span>
      <span className="wp-drow-value">{value}</span>
    </div>
  );
}

// ================================================================
// SECTION 18: SEARCH MODAL
// ================================================================

function useCitySearch() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const r = await fetch(
        `${GEO_API}?name=${encodeURIComponent(q)}&count=10&language=fa&format=json`
      );
      const j = await r.json();
      setResults((j.results || []).map((c) => ({
        id: `${c.latitude}-${c.longitude}`,
        name: c.name,
        country: c.country || '',
        admin1: c.admin1 || '',
        latitude: c.latitude,
        longitude: c.longitude,
        timezone: c.timezone || TIMEZONE,   // ✅ ذخیره تایم‌زون
      })));
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  return { results, loading, search };
}

function SearchModal({ cities, onAdd, onClose }) {
  const [q, setQ] = useState('');
  const { results, loading, search } = useCitySearch();
  const inputRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    setTimeout(() => inputRef.current?.focus(), 100);
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(q), 300);
    return () => clearTimeout(t);
  }, [q, search]);

  return (
    <div className="wp-modal-overlay" onClick={onClose}>
      <div className="wp-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wp-search-head">
          <button className="wp-search-back" onClick={onClose} aria-label="بستن">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <input
            ref={inputRef}
            type="text"
            className="wp-search-input"
            placeholder="جستجوی شهر..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && (
            <button className="wp-search-clear" onClick={() => setQ('')} aria-label="پاک">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>

        <div className="wp-search-body">
          {loading && <div className="wp-search-loading">در حال جستجو...</div>}
          {!loading && q.length >= 2 && results.length === 0 && (
            <div className="wp-search-empty">شهری با این نام پیدا نشد</div>
          )}
          {!loading && results.length > 0 && (
            <ul className="wp-search-results">
              {results.map((c) => {
                const exists = cities.some((x) => x.id === c.id);
                return (
                  <li key={c.id} className="wp-search-item">
                    <div className="wp-search-item-info">
                      <span className="wp-search-item-name">{c.name}</span>
                      <span className="wp-search-item-region">
                        {[c.admin1, c.country].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    <button
                      className={`wp-search-item-add ${exists ? 'disabled' : ''}`}
                      disabled={exists}
                      onClick={() => onAdd(c)}
                    >
                      {exists ? '✓' : '+'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {!q && (
            <div className="wp-search-hint">نام شهر را تایپ کنید (حداقل ۲ حرف)</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ================================================================
// SECTION 19: SETTINGS MODAL
// ================================================================

function SettingsModal({
  settings, onChange, cities, activeIdx,
  onRemoveCity, primaryCityId, onSetPrimaryCity, onClose,
}) {
  const update = (k, v) => onChange({ ...settings, [k]: v });

  return (
    <div className="wp-modal-overlay" onClick={onClose}>
      <div className="wp-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wp-settings-head">
          <h2>تنظیمات</h2>
          <button className="wp-search-back" onClick={onClose} aria-label="بستن">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="wp-settings-body">
          {/* ✅ Dark Mode */}
          <section className="wp-set-section">
            <h3>حالت نمایش</h3>
            <div className="wp-set-seg">
              <button className={!settings.darkMode ? 'active' : ''} onClick={() => update('darkMode', false)}>☀️ روز</button>
              <button className={settings.darkMode ? 'active' : ''} onClick={() => update('darkMode', true)}>🌙 شب</button>
            </div>
          </section>

          {/* ✅ tempUnit */}
          <section className="wp-set-section">
            <h3>واحد دما</h3>
            <div className="wp-set-seg">
              <button className={settings.tempUnit === 'celsius' ? 'active' : ''} onClick={() => update('tempUnit', 'celsius')}>سلسیوس °C</button>
              <button className={settings.tempUnit === 'fahrenheit' ? 'active' : ''} onClick={() => update('tempUnit', 'fahrenheit')}>فارنهایت °F</button>
            </div>
          </section>

          {/* ✅ windUnit */}
          <section className="wp-set-section">
            <h3>واحد باد</h3>
            <div className="wp-set-seg">
              <button className={settings.windUnit === 'kmh' ? 'active' : ''} onClick={() => update('windUnit', 'kmh')}>km/h</button>
              <button className={settings.windUnit === 'ms' ? 'active' : ''} onClick={() => update('windUnit', 'ms')}>m/s</button>
              <button className={settings.windUnit === 'mph' ? 'active' : ''} onClick={() => update('windUnit', 'mph')}>mph</button>
            </div>
          </section>

          {/* ✅ timeFormat */}
          <section className="wp-set-section">
            <h3>قالب ساعت</h3>
            <div className="wp-set-seg">
              <button className={settings.timeFormat === '24' ? 'active' : ''} onClick={() => update('timeFormat', '24')}>۲۴ ساعته</button>
              <button className={settings.timeFormat === '12' ? 'active' : ''} onClick={() => update('timeFormat', '12')}>۱۲ ساعته</button>
            </div>
          </section>

          {/* ✅ شهرهای ذخیره‌شده با قابلیت تعیین شهر اصلی */}
          <section className="wp-set-section">
            <h3>شهرهای ذخیره‌شده</h3>
            <ul className="wp-set-cities">
              {cities.map((c, i) => {
                const isPrimary = primaryCityId === c.id;
                const isActive = i === activeIdx;
                return (
                  <li
                    key={c.id}
                    className={`wp-set-city ${isActive ? 'current' : ''} ${isPrimary ? 'is-primary' : ''}`}
                  >
                    <button
                      className={`wp-set-star ${isPrimary ? 'active' : ''}`}
                      onClick={() => onSetPrimaryCity(c.id)}
                      aria-label={isPrimary ? 'شهر اصلی' : 'تعیین به عنوان شهر اصلی'}
                      title={isPrimary ? 'شهر اصلی' : 'تعیین به عنوان شهر اصلی'}
                    >
                      {isPrimary ? (
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                          <path
                            d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>

                    <div className="wp-set-city-info">
                      <span className="wp-set-city-name">{c.name}</span>
                      <span className="wp-set-city-meta">
                        {isPrimary && <span className="wp-set-city-badge">شهر اصلی</span>}
                        {/*{isActive && <span className="wp-set-city-badge subtle">فعلی</span>}*/}
                      </span>
                    </div>

                    {cities.length > 1 && (
                      <button
                        className="wp-set-city-del"
                        onClick={() => onRemoveCity(c.id)}
                        aria-label="حذف"
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                          <path
                            d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            
          </section>
        </div>
      </div>
    </div>
  );
}